/**
 * Loads the Razorpay checkout.js script on demand (once per page load)
 * and returns an `openRazorpay(options)` function.
 *
 * Flow (one-time order):
 *   1. Backend POST /checkout/create-order → { rzp_order_id, amount, currency, key_id }
 *   2. openRazorpay(...)  → Razorpay modal opens
 *   3. handler(response)  → { razorpay_order_id, razorpay_payment_id, razorpay_signature }
 *   4. POST /checkout with those three fields to complete payment
 *
 * Flow (autopay subscription — monthly/yearly plans):
 *   1. create-order → { autopay: true, razorpay_subscription_id, ... }
 *   2. openRazorpay with subscription_id (not order_id)
 *   3. handler → { razorpay_subscription_id, razorpay_payment_id, razorpay_signature }
 *   4. POST /checkout with subscription fields
 */

const SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

/** Razorpay expects digits with country prefix — bare 10 digits default to +91 (India). */
export function formatRazorpayContact(merchant) {
  if (!merchant) return '';
  const code = (merchant.mobile_code || merchant.whatsapp_code || '').replace(/\D/g, '');
  const num = (merchant.mobile || merchant.whatsapp_number || '').replace(/\D/g, '');
  if (!num) return '';
  if (code && num.startsWith(code)) return num;
  return code ? `${code}${num}` : num;
}

function loadScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) { resolve(); return; }
    const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.onload  = resolve;
    s.onerror = () => reject(new Error('Failed to load Razorpay checkout script'));
    document.body.appendChild(s);
  });
}

/**
 * @param {object} opts
 * @param {string}   opts.key_id
 * @param {number}   opts.amount        — smallest currency unit (paise for INR)
 * @param {string}   opts.currency      — 'INR' | 'USD'
 * @param {string}  [opts.rzp_order_id]         — one-time order checkout
 * @param {string}  [opts.rzp_subscription_id]  — autopay subscription checkout
 * @param {boolean} [opts.autopay]
 * @param {string}   opts.description
 * @param {string}  [opts.prefill_name]
 * @param {string}  [opts.prefill_email]
 * @param {string}  [opts.prefill_contact]
 * @param {function} opts.onSuccess(rzpResponse)
 * @param {function} opts.onDismiss()
 */
async function openRazorpay(opts) {
  await loadScript();

  const checkoutOpts = {
    key:          opts.key_id,
    amount:       opts.amount,
    currency:     opts.currency,
    name:         'Glammify',
    description:  opts.description || 'Subscription payment',
    image:        '/assets/img/glammify-logo.png',
    prefill: {
      name:    opts.prefill_name    || '',
      email:   opts.prefill_email   || '',
      contact: opts.prefill_contact || '',
    },
    theme:  { color: '#6318C6' },
    handler: (response) => opts.onSuccess(response),
    modal:   { ondismiss: () => opts.onDismiss && opts.onDismiss() },
  };

  // Autopay uses Razorpay Subscriptions API — subscription_id instead of order_id
  if (opts.autopay && opts.rzp_subscription_id) {
    checkoutOpts.subscription_id = opts.rzp_subscription_id;
    // Subscription auth supports card and eMandate only — UPI autopay not enabled.
    checkoutOpts.config = {
      display: {
        hide: [{ method: 'wallet' }, { method: 'netbanking' }, { method: 'paylater' }],
        preferences: { show_default_blocks: true },
      },
    };
  } else {
    checkoutOpts.order_id = opts.rzp_order_id;
    // One-time order: show every method enabled on the Razorpay dashboard.
    checkoutOpts.method = {
      netbanking: true,
      card: true,
      wallet: true,
      upi: true,
      emi: true,
      paylater: true,
    };
  }

  const rzp = new window.Razorpay(checkoutOpts);
  rzp.open();
}

export function useRazorpay() {
  return { openRazorpay };
}
